<script setup lang="ts">
import { UIButton } from '@gohighlevel/ghl-ui'
import { PropType, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  IRecipient,
  APP_STORE_META,
  upgradeRecipientToPrimarySigner,
  RecipientEntityTypeEnum,
} from '@gohighlevel/ghl-proposals-common'
import { Trash01Icon, Pencil01Icon } from '@gohighlevel/ghl-icons/24/outline'
import { useAppStore } from '@/store/app'
import { useElementOption } from '@/composable/elementOptions'
import { useBuilderStore } from '@/store/builder'
import UserAvatar from '@/components/common/UserAvatar.vue'
import { useEmitter } from '@/composable/useEmitter'
import { ContactService } from '@/service/ContactService'

const { updateDocumentOption } = useElementOption()
const store = useBuilderStore()
const appStore = useAppStore()
const emitter = useEmitter()
const { t } = useI18n()

const props = defineProps({
  recipient: { type: Object as PropType<IRecipient> },
  delete: { type: Boolean, default: false },
})

const deleteRecipient = (recipientId: string) => {
  if (recipientId) {
    let recipients = appStore.document?.recipients?.filter((el: any) => {
      return el.id !== recipientId
    })

    const primaryRecipient = recipients?.find((el: any) => {
      return el.isPrimary === true
    })

    // Next in-line signer becomes the primary client if the deleted recipient is the primary client
    if (recipients.length > 0 && !primaryRecipient) {
      recipients = updateNewPrimaryRecipient(recipients)
    }
    updateDocumentOption(APP_STORE_META.RECIPIENTS, recipients)
    store.syncRecipient()
  }
}

/**
 * Calculates the next primary client if the current primary client is not present in recipient list
 * @param recipients
 */
const updateNewPrimaryRecipient = (recipients: IRecipient[]): IRecipient[] => {
  const candidateRecipients = recipients.filter((el: any) => {
    return el.entityName === 'contacts'
  })

  if (candidateRecipients.length > 0) {
    // Upgrade the first candidate recipient to primary
    const upgradedRecipient = upgradeRecipientToPrimarySigner(
      candidateRecipients[0]
    )

    const index = recipients.indexOf(candidateRecipients[0])
    if (index > -1) {
      // Update the recipient at the found index
      recipients[index] = upgradedRecipient

      // Add the new primary client to the recipients list being shown under primary client dropdown as well
      emitter.emit('contact-added', upgradedRecipient)
    }
  }

  return recipients
}
const loading = ref(false)
const onClickEditRecipient = async (recipientId: string) => {
  loading.value = true
  const { data } = await ContactService.getContact(recipientId)
  loading.value = false
  const contact = data.contact
  emitter.emit('open-edit-contact-model', {
    title: t('common.editClient'),
    payload: {
      firstName: contact?.firstName,
      lastName: contact?.lastName,
      email: contact?.email,
      phone: contact?.phone,
      address1: contact?.address1,
      city: contact?.city,
      countryCode: contact?.country,
      postalCode: contact?.postalCode,
      state: contact?.state,
      id: contact?.id,
    },
  })
}
</script>

<template>
  <div class="flex flex-col relative" v-if="recipient?.id">
    <UserAvatar :recipient="recipient" :show-recipient-tag="true" />
    <div
      class="flex-none absolute right-0 top-3"
      v-if="props.delete && store.isDocumentEditable"
    >
      <div class="flex gap-2">
        <UIButton
          :id="`btn_custom_${recipient?.id}`"
          v-if="recipient?.entityName === RecipientEntityTypeEnum.CONTACTS"
          @click="onClickEditRecipient(recipient?.id)"
          :circle="true"
          :quaternary="true"
          :loading="loading"
        >
          <Pencil01Icon v-if="!loading" class="w-4 h-4 cursor-pointer" />
        </UIButton>
        <UIButton
          :id="`btn_custom_${recipient?.id}`"
          @click="deleteRecipient(recipient?.id)"
          :circle="true"
          :quaternary="true"
        >
          <Trash01Icon class="w-4 h-4 cursor-pointer"
        /></UIButton>
      </div>
    </div>
  </div>
</template>
